import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useSidebar } from "../../contexts/SidebarContext";
import { useNavigate } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";
import LogoutModal from "./LogoutModal";
import Button from "./Button";
import { useVendorVerification } from "../../hooks/useVendorVerification";
import NotificationBell from "./NotificationBell";
// import { useVendorSubscription } from "../../hooks/useVendorSubscription";

const VendorHeader = () => {
  const { user, logout } = useAuth();
  const { toggleVendorMobileSidebar } = useSidebar();
  const navigate = useNavigate();
  const { isVendorVerified, isVerificationPending } = useVendorVerification();
  // const { hasActiveSubscription } = useVendorSubscription();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    setShowLogoutModal(false);
    setShowUserDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-[10000] bg-white shadow-sm border-b border-gray-200">
      <div className="px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Mobile Menu Button */}
            <button
              onClick={toggleVendorMobileSidebar}
              className="lg:hidden p-2 text-gray-400 hover:text-gray-600"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            {/* Logo */}
            <div className="flex items-center space-x-1 sm:space-x-2">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-medium text-xs sm:text-sm">PB</span>
              </div>
              <span className="text-lg sm:text-xl font-medium text-gray-900">
                PhotoBooth
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Verify Identity Button - Only show for unverified vendors */}
            {!isVendorVerified && (
              <div className="relative hidden sm:block">
                {!isVerificationPending && (
                  <div className="absolute top-0 bottom-0 left-0.5 right-0.5 bg-primary-600 rounded-full animate-ping opacity-20"></div>
                )}
                <Button 
                  onClick={() => {
                    if (!isVerificationPending) {
                      navigate('/vendor/verification');
                    }
                  }}
                  variant={isVerificationPending ? "secondary" : "primary"}
                  size="sm"
                  className={`!rounded-full !shadow-lg relative z-10 ${isVerificationPending ? 'cursor-default' : ''}`}
                  disabled={isVerificationPending}
                >
                  {isVerificationPending ? 'Pending Approval' : 'Verify your identity'}
                </Button>
              </div>
            )}

            <NotificationBell />

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                className="flex items-center space-x-1 sm:space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium text-xs sm:text-sm">
                    {user?.name ? user.name.charAt(0).toUpperCase() : "V"}
                  </span>
                </div>
                {/* <span className="text-sm font-normal hidden md:block">
                  {user?.name || "Vendor"}
                </span> */}
              </button>

              {/* User Dropdown */}
              {showUserDropdown && (
                <div className="absolute right-0 mt-2 w-44 sm:w-48 bg-white rounded-md shadow-lg py-1 z-[10001] border border-gray-200">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-normal text-gray-900">
                      {user?.name || "Vendor"}
                    </p>
                    <p className="text-xs font-light text-gray-500">
                      {user?.email || "vendor@example.com"}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowLogoutModal(true)}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />
    </header>
  );
};

export default VendorHeader;
